package or.garden.manager.features.seeds

import androidx.compose.runtime.*
import or.garden.manager.components.CardSkeleton
import or.garden.manager.domain.model.SeedData
import or.garden.manager.features.seeds.components.SeedEditView
import or.garden.manager.features.seeds.components.WebSeedCard
import org.jetbrains.compose.web.dom.*

@Composable
fun SeedsScreen(
    seeds: List<SeedData>,
    isLoading: Boolean,
    isSaving: Boolean,
    externalAddTrigger: Int,
    onSaveSeed: (SeedData?, String, String, String, String, Set<Int>, Boolean, ByteArray?, ByteArray?, () -> Unit) -> Unit,
    onDeleteSeed: (Int) -> Unit
) {
    var isSeedEditorActive by remember { mutableStateOf(false) }
    var selectedSeedForEdit by remember { mutableStateOf<SeedData?>(null) }
    
    // Editor states
    var seedName by remember { mutableStateOf("") }
    var seedVariety by remember { mutableStateOf("") }
    var seedType by remember { mutableStateOf("early") }
    var seedHarvestTime by remember { mutableStateOf("") }
    var seedSelectedMonths by remember { mutableStateOf(setOf<Int>()) }
    var seedIsHybrid by remember { mutableStateOf(false) }
    
    var seedPhotoBytes by remember { mutableStateOf<ByteArray?>(null) }
    var seedBackPhotoBytes by remember { mutableStateOf<ByteArray?>(null) }
    var seedPhotoPreview by remember { mutableStateOf<String?>(null) }
    var seedBackPhotoPreview by remember { mutableStateOf<String?>(null) }

    fun resetEditor() {
        selectedSeedForEdit = null
        seedName = ""
        seedVariety = ""
        seedType = "early"
        seedHarvestTime = ""
        seedSelectedMonths = emptySet()
        seedIsHybrid = false
        seedPhotoBytes = null
        seedBackPhotoBytes = null
        seedPhotoPreview = null
        seedBackPhotoPreview = null
    }

    LaunchedEffect(externalAddTrigger) {
        if (externalAddTrigger > 0) {
            resetEditor()
            isSeedEditorActive = true
        }
    }

    if (isSeedEditorActive) {
        SeedEditView(
            selectedSeed = selectedSeedForEdit,
            seedName = seedName,
            onSeedNameChange = { seedName = it },
            seedVariety = seedVariety,
            onSeedVarietyChange = { seedVariety = it },
            seedType = seedType,
            onSeedTypeChange = { seedType = it },
            seedHarvestTime = seedHarvestTime,
            onSeedHarvestTimeChange = { seedHarvestTime = it },
            seedSelectedMonths = seedSelectedMonths,
            onSeedSelectedMonthsChange = { seedSelectedMonths = it },
            seedIsHybrid = seedIsHybrid,
            onSeedIsHybridChange = { seedIsHybrid = it },
            seedPhotoPreview = seedPhotoPreview,
            onPhotoSelected = { bytes, url ->
                seedPhotoBytes = bytes
                seedPhotoPreview = url
            },
            seedBackPhotoPreview = seedBackPhotoPreview,
            onBackPhotoSelected = { bytes, url ->
                seedBackPhotoBytes = bytes
                seedBackPhotoPreview = url
            },
            isSaving = isSaving,
            onSave = {
                onSaveSeed(
                    selectedSeedForEdit, seedName, seedVariety, seedType, 
                    seedHarvestTime, seedSelectedMonths, seedIsHybrid, 
                    seedPhotoBytes, seedBackPhotoBytes
                ) {
                    isSeedEditorActive = false
                    resetEditor()
                }
            },
            onBack = { isSeedEditorActive = false }
        )
    } else if (isLoading) {
        Div({
            classes("grid", "grid-cols-1", "md:grid-cols-2", "xl:grid-cols-3", "gap-4", "md:gap-6")
        }) {
            repeat(6) { CardSkeleton() }
        }
    } else if (seeds.isEmpty()) {
        Div({ classes("text-center", "py-20", "text-gray-400") }) {
            I({ classes("fas", "fa-seedling", "text-4xl", "mb-4") })
            H2 { Text("Nema sačuvanih sjemenki.") }
            Button({
                classes("mt-4", "bg-emerald-600", "text-white", "px-6", "py-2", "rounded-xl", "font-bold")
                onClick {
                    resetEditor()
                    isSeedEditorActive = true
                }
            }) { Text("Dodaj prvu sjemenku") }
        }
    } else {
        Div({
            classes("grid", "grid-cols-1", "md:grid-cols-2", "xl:grid-cols-3", "gap-4", "md:gap-6")
        }) {
            seeds.forEach { seed ->
                WebSeedCard(
                    seed = seed,
                    onEdit = {
                        selectedSeedForEdit = seed
                        seedName = seed.name
                        seedVariety = seed.latinName ?: ""
                        seedType = seed.type ?: "early"
                        seedHarvestTime = seed.harvestTime?.toString() ?: ""
                        seedSelectedMonths = seed.seedlingDate?.split(",")?.mapNotNull { it.toIntOrNull() }?.toSet() ?: emptySet()
                        seedIsHybrid = seed.hybrid
                        seedPhotoBytes = null
                        seedBackPhotoBytes = null
                        seedPhotoPreview = seed.photoUrl()
                        seedBackPhotoPreview = seed.backPhotoUrl()
                        isSeedEditorActive = true
                    },
                    onDelete = { onDeleteSeed(seed.id) }
                )
            }
        }
    }
}
